import type React from "react";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import {
  CreatePost,
  type FailedPostDraft,
} from "@/components/common/CreatePost";
import { StreamSearch } from "@/features/study-group-stream/components/StreamSearch";
import type { StreamMode } from "@/features/study-group-stream/types/streamMode";
import type { SearchAuthorOption } from "@/features/study-group-stream/utils/streamSearchFilterOptions";
import type { StreamSearchCriteriaState } from "@/lib/utils/streamSearchUtils";

export type StreamModePanelHandlers = {
  onSearchMode: () => void;
  onCreateMode: () => void;
  onSearch: (searchCriteriaState: StreamSearchCriteriaState) => void;
  onExitMode: () => void;
  onOptimisticClose?: () => void;
  onCreatePost: (message: string, files?: File[]) => Promise<void>;
  onCreateError?: (error: unknown) => void;
  onOptimisticSubmitStart?: (draftId: string) => void;
  onOptimisticSubmitError?: (draft: FailedPostDraft, error: unknown) => void;
  onAuthorFilterOpen?: () => void | Promise<void>;
};

export type StreamModePanelState = {
  mode: StreamMode;
  studyGroupId: number | null;
  channelKey?: string | null;
  searchHits: number;
  authorOptions: SearchAuthorOption[];
  authorLabelById: Map<string, string>;
  isAuthorOptionsLoading?: boolean;
  canCreate?: boolean;
  createPostClassName?: string;
  failedDraft?: FailedPostDraft | null;
  showViewActions?: boolean;
};

export type StreamModePanelProps = StreamModePanelState &
  StreamModePanelHandlers;

const DEFAULT_CREATE_POST_CLASS =
  "p-2 animate-in fade-in slide-in-from-top-2 duration-200";
const SEARCH_PANEL_CLASS = DEFAULT_CREATE_POST_CLASS;

const StreamModePanelRaw: React.FC<StreamModePanelProps> = ({
  mode,
  studyGroupId,
  channelKey,
  searchHits,
  authorOptions,
  authorLabelById,
  isAuthorOptionsLoading = false,
  canCreate = true,
  createPostClassName = DEFAULT_CREATE_POST_CLASS,
  onSearch,
  onExitMode,
  onOptimisticClose,
  onCreatePost,
  onCreateError,
  onOptimisticSubmitStart,
  onOptimisticSubmitError,
  onAuthorFilterOpen,
  failedDraft,
  showViewActions = true,
}) => {
  const { t } = useTranslation();
  const isSearchMode = mode === "search";
  const isCreateMode = mode === "create";

  if (mode === "view") {
    return canCreate ? (
      <div
        className={`overflow-hidden transition-all duration-200 ${
          showViewActions ? "max-h-20 opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className={`${createPostClassName} mx-auto w-full max-w-[832px]`}>
          <CreatePost
            studyGroupId={studyGroupId}
            channelKey={channelKey}
            onCreatePost={onCreatePost}
            onCreated={onOptimisticClose ?? onExitMode}
            onError={onCreateError}
            onOptimisticSubmitStart={onOptimisticSubmitStart}
            onOptimisticSubmitError={onOptimisticSubmitError}
            initialDraft={failedDraft}
            optimisticCloseOnSubmit
            compact
            searchLikeWhenBlurred
            onCancel={onExitMode}
            cancelButtonLabel={t("common.cancel")}
          />
        </div>
      </div>
    ) : null;
  }

  if (isSearchMode) {
    return (
      <div className={SEARCH_PANEL_CLASS}>
        <StreamSearch
          onSearch={onSearch}
          onExitSearch={onExitMode}
          totalHits={searchHits}
          className="mb-0"
          authorOptions={authorOptions}
          authorLabelById={authorLabelById}
          onAuthorFilterOpen={onAuthorFilterOpen}
          isAuthorOptionsLoading={isAuthorOptionsLoading}
        />
      </div>
    );
  }

  if (isCreateMode) {
    return canCreate ? (
      <div className={`${createPostClassName} mx-auto w-full max-w-[832px]`}>
        <CreatePost
          studyGroupId={studyGroupId}
          channelKey={channelKey}
          onCreatePost={onCreatePost}
          onCreated={onOptimisticClose ?? onExitMode}
          onError={onCreateError}
          onOptimisticSubmitStart={onOptimisticSubmitStart}
          onOptimisticSubmitError={onOptimisticSubmitError}
          initialDraft={failedDraft}
          optimisticCloseOnSubmit
          autoFocus
          onCancel={onExitMode}
          cancelButtonLabel={t("common.cancel")}
        />
      </div>
    ) : null;
  }

  return null;
};

export const StreamModePanel = memo(StreamModePanelRaw);
StreamModePanel.displayName = "StreamModePanel";
